import { Form, Input } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import PhoneInput from '../../../../shared/components/inputs/phone-input/phone-input';
import Modal from '../../../../shared/components/modal/modal';
import { ValidationTrigger } from '../../../../shared/enums/validation-trigger';
import { handleServerFormErrors, mapServerErrors } from '../../../../shared/utils/error-handling/error-handling';
import { checkIfValidatePhone, validatePhone } from '../../../../shared/utils/phone-validation/phone-validation';
import { useAppSelector } from '../../../../store/hooks';
import ClientGroupsSelect from '../../../client-groups/components/client-groups-select/client-groups-select';

import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useCreateCustomerMutation, useGetCustomerQuery, useUpdateCustomerMutation } from '../../api/customers-api';
import { customersAlertId, customersFormModalId } from '../../constants/constants';
import { CustomerFormModel } from '../../models/customer-form-model';
import { selectActiveCustomerId, setActiveCustomerId } from '../../store/customers-slice';
import { useGetUserSettingsQuery } from '../../../user/api/user-api';

interface ValidationTriggerModel {
  email: ValidationTrigger;
  phone: ValidationTrigger;
}

const CustomersFormModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['customers', 'common']);
  const dispatch = useDispatch();
  const activeCustomerId = useAppSelector(selectActiveCustomerId);
  const { data: userSettings } = useGetUserSettingsQuery();

  const [form] = Form.useForm<CustomerFormModel>();

  const [postCustomer, { isLoading: isCreateCustomerLoading }] = useCreateCustomerMutation();
  const [editCustomer, { isLoading: isUpdateCustomerLoading }] = useUpdateCustomerMutation();

  const [validationTrigger, setValidationTrigger] = useState<ValidationTriggerModel>({
    email: ValidationTrigger.OnBlur,
    phone: ValidationTrigger.OnBlur,
  });

  const { data: customerData } = useGetCustomerQuery(activeCustomerId || '', {
    skip: !activeCustomerId,
  });

  const closeCustomerModal = () => {
    dispatch(closeModal(customersFormModalId));
    dispatch(setActiveCustomerId(null));
  };

  const submitForm = (formData: CustomerFormModel) => {
    const { customerFormEmail, customerFormSurname, customerName, customerPhone, ...restFormData } = formData;

    (activeCustomerId
      ? editCustomer({
          id: activeCustomerId,
          name: customerName,
          email: customerFormEmail,
          surname: customerFormSurname,
          phone: customerPhone,
          ...restFormData,
        })
      : postCustomer({
          name: customerName,
          email: customerFormEmail,
          surname: customerFormSurname,
          phone: customerPhone,
          ...restFormData,
        })
    )
      .unwrap()
      .then(() => {
        closeCustomerModal();
        dispatch(
          setAlert({
            id: customersAlertId,
            type: 'success',
            title: activeCustomerId ? t('customerForm.message.editSuccess') : t('customerForm.message.createSuccess'),
          }),
        );
      })
      .catch(error => {
        handleServerFormErrors(
          form,
          mapServerErrors(error.data?.errors, {
            name: 'customerName',
            surname: 'customerFormSurname',
            email: 'customerFormEmail',
            phone: 'customerPhone',
          }),
        );
      });
  };

  const formSubmit = () => {
    if (validationTrigger.email === ValidationTrigger.OnBlur && validationTrigger.phone === ValidationTrigger.OnBlur) {
      setValidationTrigger({
        email: ValidationTrigger.OnChange,
        phone: ValidationTrigger.OnChange,
      });
    }
    form.submit();
  };

  const initialValues: CustomerFormModel = useMemo(() => {
    return {
      customerName: customerData?.name || '',
      customerFormSurname: customerData?.surname || '',
      customerFormEmail: customerData?.email || '',
      customerPhone: customerData?.phone || '',
      groups: customerData?.groups || [],
    };
  }, [customerData]);

  useEffect(() => {
    form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <Modal
      title={activeCustomerId ? t('customerForm.editCustomer') : t('customerForm.createCustomer')}
      onCancel={() => (!isCreateCustomerLoading || !isUpdateCustomerLoading) && closeCustomerModal()}
      isLoading={isCreateCustomerLoading || isUpdateCustomerLoading}
      onOk={formSubmit}
      isOpen={isOpen}
      afterClose={afterClose}
      formId={customersFormModalId}
    >
      <Form id={customersFormModalId} form={form} layout="vertical" initialValues={initialValues} onFinish={submitForm}>
        <Form.Item
          name="customerName"
          label={t('label.name', { ns: 'common' })}
          rules={[{ required: true, message: t('errors.nameRequired', { ns: 'common' }) }]}
        >
          <Input placeholder={t('label.name', { ns: 'common' })} />
        </Form.Item>
        <Form.Item
          name="customerFormSurname"
          label={t('label.surname', { ns: 'common' })}
          rules={[{ required: true, message: t('customerForm.validationError.surnameRequired') }]}
        >
          <Input placeholder={t('label.surname', { ns: 'common' })} />
        </Form.Item>
        <Form.Item
          name="customerFormEmail"
          label={t('label.email', { ns: 'common' })}
          validateTrigger={validationTrigger.email}
          rules={[
            { required: true, message: t('errors.emailRequired', { ns: 'common' }) },
            {
              type: 'email',
              message: t('errors.emailFormat', { ns: 'common' }),
            },
          ]}
          normalize={value => value.trim()}
        >
          <Input placeholder={t('label.email', { ns: 'common' })} />
        </Form.Item>
        <Form.Item
          name="customerPhone"
          label={t('label.phone', { ns: 'common' })}
          validateTrigger={validationTrigger.phone}
          rules={[
            {
              validator: (_rule, value: string) =>
                checkIfValidatePhone(value, customerData?.phone || '') ? validatePhone(value) : Promise.resolve(),
              message: t('errors.phoneFormat', { ns: 'common' }),
            },
          ]}
        >
          <PhoneInput
            value={customerData?.phone || ''}
            inputLabel={t('label.phone', { ns: 'common' })}
            countryCode={userSettings?.country.toUpperCase()}
          />
        </Form.Item>
        <Form.Item label={t('label.groups', { ns: 'common' })} name="groups">
          <ClientGroupsSelect mode="multiple" maxTagCount={4} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const CustomerFormModal = (): JSX.Element => {
  return <LazyModal modalId={customersFormModalId} Component={CustomersFormModalContent} />;
};

export default CustomerFormModal;
